import { sortBy } from 'lodash-es';

import type { Security } from '@talos/kyoko';

export const PREDICTION_OUTCOMES_ODDS_SORT_MAX = 20;

export function sortPredictionOutcomesByDisplaySymbol(outcomes: Security[]): Security[] {
  return sortBy(outcomes, outcome => outcome.DisplaySymbol ?? outcome.Symbol);
}

export interface SortablePredictionOutcome {
  symbol: string;
  label: string;
}

export function shouldSortPredictionOutcomeRowsByOdds(rowCount: number): boolean {
  return rowCount > 0 && rowCount < PREDICTION_OUTCOMES_ODDS_SORT_MAX;
}

function parseOdds(odds: string | number | undefined): number | undefined {
  if (odds == null || odds === '') {
    return undefined;
  }
  const numeric = Number(odds);
  return Number.isFinite(numeric) ? numeric : undefined;
}

/**
 * Sorts outcome rows by chance % (odds), highest first. Outcomes without a price yet
 * (no market data) sink to the bottom. Ties — and the priceless group — fall back to alphabetical
 * label order so the ordering is stable and deterministic.
 */
export function sortPredictionOutcomeRowsByOdds<T extends SortablePredictionOutcome>(
  rows: T[],
  oddsBySymbol: ReadonlyMap<string, string | number | undefined>
): T[] {
  return rows.toSorted((a, b) => {
    const oddsA = parseOdds(oddsBySymbol.get(a.symbol));
    const oddsB = parseOdds(oddsBySymbol.get(b.symbol));

    if (oddsA != null && oddsB != null) {
      return oddsB - oddsA || a.label.localeCompare(b.label);
    }
    if (oddsA != null) {
      return -1;
    }
    if (oddsB != null) {
      return 1;
    }
    return a.label.localeCompare(b.label);
  });
}
